// The card surface, and the head that names it (#11426).
//
// The card is the brightest thing on screen and the page ground sits a step
// below it. That tonal step is what separates them — not a heavy border, and
// never a grey box on a grey page. Hairlines, never boxes.

import React from "react";
import { Tooltip } from "@mui/material";
import { StillPageContext } from "./stillPage";

export interface CardProps {
  children?: React.ReactNode;
  /** Removes the padding so a table can reach the card's edges. */
  flush?: boolean;
  /** The tighter padding a dashboard widget takes, so its cell goes to its data. */
  dense?: boolean;
  /**
   * The tallest the card grows, in px. Its children then stack in a column, so one of them can
   * take what is left and scroll (#13370).
   */
  maxHeight?: number;
}

/**
 * Renders nothing when it holds nothing, so a record with no data for a
 * component contributes no height to the page.
 */
export const Card: React.FC<CardProps> = ({
  children,
  flush,
  dense,
  maxHeight,
}) => {
  // On a still page (Appearance's previews, #13370 round 36e: "the cards shouldn't have shading
  // on the outside") a card lies flat: a stronger hairline and no lift.
  const still = React.useContext(StillPageContext);
  if (React.Children.count(children) === 0) return null;

  return (
    // `data-card`: the card's own box, which Appearance's Overview holds to its cell and measures
    // for its "more content" fade (#13370).
    <div
      data-card=""
      style={{
        backgroundColor: "var(--fp-surface)",
        border: `1px solid ${still ? "var(--fp-border-strong)" : "var(--fp-border)"}`,
        borderRadius: "var(--fp-card-r)",
        boxShadow: still ? "none" : "var(--fp-lift)",
        padding: flush
          ? undefined
          : dense
            ? "var(--fp-card-pad-dense)"
            : "var(--fp-card-pad)",
        minWidth: 0,
        ...(maxHeight === undefined
          ? {}
          : {
              maxHeight,
              display: "flex",
              flexDirection: "column" as const,
            }),
      }}
    >
      {children}
    </div>
  );
};

export interface SectionHeadProps {
  title: string;
  /** A quiet qualifier — what the count counts, what it is as of. */
  sub?: React.ReactNode;
  /** A doorway, rendered at the right. */
  action?: React.ReactNode;
  /** The heading's level in the page outline: 3 where the card sits under a section's h2. */
  level?: 2 | 3;
  /**
   * `widget`: a dashboard widget's quiet head, which stands back from the widget's data: one line
   * in the meta size and secondary ink, truncated, with the full name in a tooltip.
   */
  variant?: "section" | "widget";
  /** In place of the title, a field editing it (a dashboard widget's inline rename). */
  editor?: React.ReactNode;
  /**
   * Set right after the title, in the title's slot (a dashboard widget's "Added by assistant"):
   * it gives way before the title does, collapsing to nothing while the title keeps its width.
   */
  titleTrail?: React.ReactNode;
}

export const SectionHead: React.FC<SectionHeadProps> = ({
  title,
  sub,
  action,
  level = 2,
  variant = "section",
  editor,
  titleTrail,
}) => {
  const Heading = level === 3 ? "h3" : "h2";
  const widget = variant === "widget";
  const heading = (
    <Heading
      style={{
        fontFamily: "var(--fp-font-ui)",
        fontSize: widget ? "var(--fp-t-meta)" : "var(--fp-t-lede)",
        fontWeight: (widget
          ? "var(--fp-w-strong)"
          : "var(--fp-w-head)") as unknown as number,
        letterSpacing: widget ? "var(--fp-track-body)" : "var(--fp-track-head)",
        color: widget ? "var(--fp-ink-2)" : "var(--fp-ink)",
        margin: 0,
        // A title wraps between its words, never inside one, whatever the card around it
        // allows its content (#13370).
        overflowWrap: "normal",
        wordBreak: "normal",
        ...(widget
          ? {
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }
          : {}),
      }}
    >
      {title}
    </Heading>
  );
  return (
    <div
      style={{
        display: "flex",
        // An editor may take two lines (a field and its consequence): the head grows downward
        // and the rest of it stays on the first line.
        alignItems: widget ? (editor ? "flex-start" : "center") : "baseline",
        gap: "var(--fp-gap-tight)",
        marginBottom: widget ? "var(--fp-gap-hair)" : "var(--fp-gap-tight)",
      }}
    >
      {editor ? (
        // In the title's own type, so the head keeps its size while it is edited.
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: "var(--fp-font-ui)",
            fontSize: widget ? "var(--fp-t-meta)" : "var(--fp-t-lede)",
            color: widget ? "var(--fp-ink-2)" : "var(--fp-ink)",
          }}
        >
          {editor}
        </span>
      ) : widget ? (
        <Tooltip title={title} placement="top-start">
          {heading}
        </Tooltip>
      ) : (
        heading
      )}
      {titleTrail && !editor ? (
        <span
          style={{
            // Only the room the title leaves: no basis of its own, growing to its own width at
            // most, so the title is laid out whole first and the trail takes what is left.
            flex: "1 1 0",
            maxWidth: "max-content",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {titleTrail}
        </span>
      ) : null}
      {sub ? (
        <span
          style={{
            fontFamily: "var(--fp-font-ui)",
            fontSize: "var(--fp-t-meta)",
            color: "var(--fp-ink-3)",
            // Wraps at its spaces; only a word wider than the card breaks.
            overflowWrap: "break-word",
            minWidth: 0,
          }}
        >
          {sub}
        </span>
      ) : null}
      {action ? (
        // Marked so an editor's layer over the card can keep room beside it (Appearance, #13370).
        <span data-section-head-action="" style={{ marginLeft: "auto" }}>
          {action}
        </span>
      ) : null}
    </div>
  );
};

/** The overline a rail group carries — "Portfolios", "Open deals". */
export const Overline: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      fontFamily: "var(--fp-font-ui)",
      fontSize: "var(--fp-t-micro)",
      fontWeight: "var(--fp-w-head)" as unknown as number,
      letterSpacing: "var(--fp-track-caps)",
      textTransform: "uppercase",
      color: "var(--fp-ink-3)",
      marginBottom: "var(--fp-gap-hair)",
    }}
  >
    {children}
  </div>
);

/** The rail's reading unit: ONE anatomy per card (#11426, rounds 8–9).
 *  In a ~230px rail no split of the width fits both a three-word label and
 *  an unwrappable chip, so EVERY row stacks — the label as an overline,
 *  the value beneath at full rail width, a hairline between rows. The
 *  two-column label/value split lives on wide surfaces (the Facts tab);
 *  anatomy varies by context, never within a card. */
export const KeyValue: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div
    style={{
      padding: "var(--fp-gap-hair) 0 var(--fp-gap-tight)",
      borderTop: "1px solid var(--fp-border)",
      fontFamily: "var(--fp-font-ui)",
      fontSize: "var(--fp-t-body)",
      lineHeight: "var(--fp-lh-body)",
    }}
  >
    <span
      data-key-value-label=""
      style={{
        display: "block",
        fontSize: "var(--fp-t-micro)",
        fontWeight: "var(--fp-w-head)" as unknown as number,
        letterSpacing: "var(--fp-track-caps)",
        textTransform: "uppercase",
        color: "var(--fp-ink-3)",
        marginBottom: "var(--fp-gap-hair)",
      }}
    >
      {label}
    </span>
    {/* A value with no spaces (an identifier) still wraps inside the row
        rather than running past the card (#13369). */}
    <span
      style={{
        display: "block",
        color: "var(--fp-ink)",
        minWidth: 0,
        overflowWrap: "break-word",
      }}
    >
      {children}
    </span>
  </div>
);
